<script setup lang="ts">
    import { initWebPush } from '@/plugins/webpush'

    const authStore = useAuthStore()
    const jwtToken = authStore.token

    const isSupported = ref(false)
    const permissionState = ref<'default' | 'granted' | 'denied'>('default')
    const isSubscribed = ref(false)
    const loading = ref(false)
    const message = ref('')
    let webPushClient: any = null

    // Prüfe initialen Zustand
    onMounted(async () => {
        try {
            isSupported.value = 'Notification' in window && 'serviceWorker' in navigator
            permissionState.value = Notification.permission

            if (!isSupported.value) {
                message.value = 'Dieser Browser unterstützt keine Push-Benachrichtigungen.'
                return
            }

            const registration = await navigator.serviceWorker.getRegistration('/js/webpush-sw.js')
            const sub = await registration?.pushManager.getSubscription()
            if (sub) {
                isSubscribed.value = true
                message.value = 'Push-Benachrichtigungen sind aktiv.'
            }
        } catch (err) {
            console.error('WebPush check failed:', err)
        }
    })

    // Wird durch Klick ausgelöst
    async function enablePush() {
        if (!isSupported.value) return
        loading.value = true
        message.value = ''

        try {
            webPushClient = await initWebPush(
                'BACTatYpKIpNqGPlSmGmh1pyFqLusIQ2ORBbAb4V44v3ksR9l9fCfcVuws2g9RSv5EJVSYE4eEGUGSztL03VJT4',
                '/webpush/',
                jwtToken
            )

            const state = await webPushClient.getPermissionState()
            permissionState.value = state

            if (state === 'granted') {
                const sub = await webPushClient.getSubscription()
                if (!sub) await webPushClient.subscribe()
                isSubscribed.value = true
                message.value = 'Benachrichtigungen erfolgreich aktiviert ✅'
            } else if (state === 'denied') {
                message.value =
                    'Du hast Benachrichtigungen blockiert. Bitte erlaube sie in den Browser-Einstellungen.'
            } else {
                message.value = 'Bitte erlaube Benachrichtigungen im Browser-Dialog.'
            }
        } catch (err) {
            console.error('Push subscription failed:', err)
            message.value = 'Fehler beim Aktivieren der Benachrichtigungen.'
        } finally {
            loading.value = false
        }
    }

    // Optional: Abo wieder entfernen
    async function disablePush() {
        try {
            const registration = await navigator.serviceWorker.getRegistration('/js/webpush-sw.js')
            const sub = await registration?.pushManager.getSubscription()
            if (sub) {
                await sub.unsubscribe()
                isSubscribed.value = false
                message.value = 'Benachrichtigungen wurden deaktiviert.'
            }
        } catch (err) {
            console.error('Unsubscribe failed:', err)
            message.value = 'Fehler beim Deaktivieren der Benachrichtigungen.'
        }
    }
</script>

<template>
    <v-card class="mx-auto">
        <v-card-title class="text-h6 font-semibold"> Push-Benachrichtigungen </v-card-title>

        <v-card-subtitle class="text-body-2 mb-4">
            Erhalte sofortige Benachrichtigungen über wichtige Ereignisse.
        </v-card-subtitle>

        <v-divider class="mb-4"></v-divider>
        <v-card-text>
            <v-alert
                v-if="!isSupported"
                type="warning"
                variant="tonal"
                title="Nicht unterstützt"
                text="Dieser Browser unterstützt keine Push-Benachrichtigungen."
            />

            <div v-else>
                <v-row align="center" justify="space-between" class="mb-4">
                    <v-col cols="auto">
                        <p class="text-body-2 text-medium-emphasis mb-1">Aktueller Status:</p>
                        <p class="text-body-1 font-medium">
                            <span
                                v-if="permissionState === 'granted' && isSubscribed"
                                class="text-success"
                            >
                                Aktiviert
                            </span>
                            <span v-else-if="permissionState === 'denied'" class="text-error">
                                Blockiert
                            </span>
                            <span v-else> Nicht aktiviert </span>
                        </p>
                    </v-col>

                    <v-col cols="auto">
                        <v-btn
                            v-if="!isSubscribed"
                            color="primary"
                            :loading="loading"
                            @click="enablePush"
                        >
                            Aktivieren
                        </v-btn>

                        <v-btn
                            v-else
                            color="error"
                            variant="tonal"
                            :loading="loading"
                            @click="disablePush"
                        >
                            Deaktivieren
                        </v-btn>
                    </v-col>
                </v-row>

                <v-slide-y-transition>
                    <v-alert
                        v-if="message"
                        class="mt-2"
                        :type="
                            message.includes('Fehler')
                                ? 'error'
                                : message.includes('erfolgreich')
                                  ? 'success'
                                  : 'info'
                        "
                        variant="tonal"
                    >
                        {{ message }}
                    </v-alert>
                </v-slide-y-transition>

                <v-divider class="my-4"></v-divider>

                <p class="text-caption text-medium-emphasis">
                    Berechtigungsstatus im Browser: <strong>{{ permissionState }}</strong>
                </p>
            </div>
        </v-card-text>
    </v-card>
</template>

<style scoped>
    .text-success {
        color: rgb(var(--v-theme-success));
    }
    .text-error {
        color: rgb(var(--v-theme-error));
    }
</style>
